import { createContext, Dispatch, useContext, useEffect, useReducer, ReactNode } from "react";
import { AmplifyUser } from "@aws-amplify/ui";
import { setAuthTokenHeaders } from "../../../api/api";
import { getCustomer } from "../../../api/customer";
import { capture } from "../../../shared/capture";
import { AppStateActionType } from "../../contexts/app/reducer";
import { Analytics } from "../../shared/analytics";
import { fetchUserKey, fetchUserToken } from "../../shared/util";
import { AppState, AppStateAction, initialState, reducer } from "./reducer";
import { useIsUserInSandbox } from "../../shared/useIsUserInSandbox";
import { updateBaseURL } from "../../../api/api";
import { updateBaseURL as updateBaseURLOSS } from "../../../api/apiOSS";

interface IAppStateContext {
  state: AppState;
  dispatch: Dispatch<AppStateAction>;
}

export const AppStateContext = createContext<IAppStateContext>({
  state: initialState,
  dispatch: () => null,
});

export const AppStateProvider = ({
  user,
  children,
}: {
  user: AmplifyUser | undefined;
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isSandboxMap } = useIsUserInSandbox(state);

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await fetchUserToken();
        setAuthTokenHeaders(token);

        const customer = await getCustomer();
        const username = user?.username || "";
        const keys = await fetchUserKey(username);

        Analytics.identify(customer.id);

        updateBaseURL(isSandboxMap[username]);
        updateBaseURLOSS(isSandboxMap[username]);

        dispatch({
          type: AppStateActionType.update,
          newState: {
            authToken: token,
            customer,
            user,
            isLoaded: true,
            keys,
          },
        });
      } catch (err) {
        capture.error(err, { extra: { context: `app.state.init` } });
      }
    }
    fetchData();
  }, [isSandboxMap]);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
  );
};

export const useAppContext = () => useContext(AppStateContext);
